import { zoneAvoid, zoneCadre, zoneClub, zoneFlip, zonePeople } from "@/mocks/gossipData";
import { Chip, SectionHead } from "./ui";

type Tone = "primary" | "secondary";
const toneMap = {
  primary: {
    icon: "bg-primary-100 text-primary-700",
    num: "text-primary-400",
    more: "text-primary-600 hover:text-primary-700",
    dot: "bg-primary-500",
  },
  secondary: {
    icon: "bg-secondary-100 text-secondary-700",
    num: "text-secondary-400",
    more: "text-secondary-700 hover:text-secondary-800",
    dot: "bg-secondary-500",
  },
};

function ZonePanel({
  id,
  data,
  countLabel,
}: {
  id: string;
  data: { title: string; kicker: string; desc: string; accent: Tone; items: { title: string; abstract: string; meta: string }[] };
  countLabel: string;
}) {
  const t = toneMap[data.accent];
  return (
    <div id={id} className="flex scroll-mt-24 flex-col rounded-xl border border-background-200 bg-background-50 p-5 md:p-6">
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-3">
          <span className={`inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-lg ${t.icon}`}>
            <i className={`${data.accent === "secondary" ? "ri-shield-check-line" : "ri-vip-crown-2-line"} text-xl`}></i>
          </span>
          <div>
            <h3 className="font-heading text-xl font-extrabold text-foreground-950">{data.title}</h3>
            <span className="text-xs text-foreground-500">{data.kicker}</span>
          </div>
        </div>
        <span className="inline-flex shrink-0 items-center gap-1 rounded-md bg-background-100 px-2 py-1 text-[11px] font-semibold text-foreground-600">
          {countLabel}
        </span>
      </div>
      <p className="mt-3 text-sm leading-relaxed text-foreground-600">{data.desc}</p>
      <div className="mt-4 flex flex-1 flex-col divide-y divide-background-200">
        {data.items.map((it, i) => (
          <a key={it.title} href="#topic" className="group flex gap-3 py-3 first:pt-1">
            <span className={`font-heading text-lg font-black leading-6 ${t.num}`}>0{i + 1}</span>
            <span className="min-w-0 flex-1">
              <span className="flex flex-wrap items-center gap-2">
                <span className="text-sm font-bold leading-snug text-foreground-900 group-hover:text-primary-700">{it.title}</span>
              </span>
              <span className="mt-1 block text-sm leading-relaxed text-foreground-600">{it.abstract}</span>
            </span>
          </a>
        ))}
      </div>
      <div className="mt-3 flex items-center justify-between border-t border-background-200 pt-3">
        <span className="text-[11px] text-foreground-400">
          {data.items[data.items.length - 1]?.meta}
        </span>
        <a href="#report" className={`inline-flex items-center gap-1 text-xs font-bold ${t.more}`}>
          查看更多 <i className="ri-arrow-right-line"></i>
        </a>
      </div>
    </div>
  );
}

export function ZoneSection() {
  return (
    <>
      <section className="bg-background-100">
        <div className="mx-auto max-w-[1280px] px-4 py-14 md:px-6">
          <SectionHead
            index="02"
            kicker="人物与口碑 · 横向精读"
            title="光环之内，与光环之外"
            desc="掌声和滤镜归他们，责任和后果也得说清。有人争名，有人避雷，两种站位的校园剧本都在这里。"
            tone="secondary"
          />
          <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
            <ZonePanel id="people" data={zonePeople} countLabel="人物争议 · 1286 条" />
            <ZonePanel id="avoid" data={zoneAvoid} countLabel="避雷名单 · 3412 条" />
          </div>
        </div>
      </section>

      <section className="bg-background-50">
        <div className="mx-auto max-w-[1280px] px-4 py-14 md:px-6">
          <SectionHead
            index="03"
            kicker="组织运行 · 制度体检"
            title="干部与社团，账得算明白"
            desc="换届、评优、考勤、经费——组织的信任，就是从流程透明里长出来的；糊弄其中任何一环，争议都会找上门。"
            tone="primary"
          />
          <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
            <ZonePanel id="cadre" data={zoneCadre} countLabel="干部争议 · 896 条" />
            <ZonePanel id="club" data={zoneClub} countLabel="社团瓜 · 1150 条" />
          </div>
        </div>
      </section>
    </>
  );
}

export function FlipSection() {
  const t = toneMap.primary;
  return (
    <section id="flip" className="scroll-mt-24 bg-background-100">
      <div className="mx-auto max-w-[1280px] px-4 py-14 md:px-6">
        <SectionHead
          index="04"
          kicker="人设塌房 · 全程回放"
          title="口碑翻车现场，时间线都替你留好"
          desc="把话说得太满、把事做得太绝，翻车时连本带利。这里只做客观回放，是非留给你自己判断。"
          tone="accent"
          right={
            <span className="inline-flex items-center gap-1.5 rounded-full bg-accent-100 px-3 py-1.5 text-xs font-bold text-accent-900">
              <i className="ri-restart-line"></i> 支持反转与澄清
            </span>
          }
        />
        <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
          {zoneFlip.items.map((it, i) => (
            <a
              key={it.title}
              href="#report"
              className="group flex gap-4 rounded-xl border border-background-200 bg-background-50 p-5 transition hover:border-accent-300 hover:bg-accent-50/40"
            >
              <div className="flex flex-col items-center">
                <span className={`inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full font-heading text-sm font-black text-background-50 ${t.dot}`}>
                  {i + 1}
                </span>
                <span className="mt-2 w-px flex-1 bg-background-200"></span>
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  <Chip tone="accent">{it.meta}</Chip>
                  <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-primary-600">
                    <i className="ri-film-line"></i> 全程记录
                  </span>
                </div>
                <h3 className="mt-2 font-heading text-base font-extrabold leading-snug text-foreground-950 group-hover:text-primary-700">
                  {it.title}
                </h3>
                <p className="mt-1.5 text-sm leading-relaxed text-foreground-600">{it.abstract}</p>
                <span className="mt-3 inline-flex items-center gap-1 text-xs font-bold text-accent-600 opacity-0 transition group-hover:opacity-100">
                  打开完整回放 <i className="ri-arrow-right-line"></i>
                </span>
              </div>
            </a>
          ))}
        </div>
      </div>
    </section>
  );
}